// VFX Config Service
// Service to fetch VFX configuration from server with ETag support and polling

import { API_CONFIG, getApiUrl } from '@/config/apiConfig';
import { OFFLINE_CACHE_KEYS, readOfflineApiCache, writeOfflineApiCache } from '@/services/apiOfflineCache';
import type { VFXConfigResponse } from '../types/vfxConfig';

const POLLING_INTERVAL = 30 * 60 * 1000; // 30 minutes
const POLLING_INTERVAL_ENV = import.meta.env.VITE_VFX_POLLING_INTERVAL;
const POLLING_INTERVAL_MS = POLLING_INTERVAL_ENV
  ? parseInt(POLLING_INTERVAL_ENV, 10) * 1000
  : POLLING_INTERVAL;
const FETCH_DEDUPE_WINDOW_MS = 5 * 1000;

export interface VFXConfigFetchResult {
  success: boolean;
  config: VFXConfigResponse | null;
  etag: string | null;
  error: string | null;
  notModified: boolean;
}

class VFXConfigService {
  private etag: string | null = null;
  private lastFetchTime: number = 0;
  /** Reuse the same in-flight fetch across concurrent callers (e.g. StrictMode double-mount). */
  private pendingFetchPromise: Promise<VFXConfigFetchResult> | null = null;
  /** Last resolved result used for short dedupe window after a successful fetch. */
  private lastResolvedResult: VFXConfigFetchResult | null = null;

  /**
   * Last successful GET /api/vfx-config body from localStorage (offline fallback).
   */
  private tryOfflineVfxConfig(): VFXConfigFetchResult | null {
    const cached = readOfflineApiCache<{ success?: boolean; data?: unknown }>(
      OFFLINE_CACHE_KEYS.VFX_CONFIG,
    );
    if (!cached?.success || !cached.data || typeof cached.data !== 'object') {
      return null;
    }
    const data = cached.data as Record<string, unknown>;
    const bg = data.background as Record<string, unknown> | undefined;
    if (bg?.theme !== undefined) {
      const rawTheme = bg.theme;
      if (typeof rawTheme === 'string' && !isNaN(Number(rawTheme))) {
        bg.theme = Number(rawTheme);
      }
    }
    if (!this.validateConfig(data)) {
      return null;
    }
    return {
      success: true,
      config: data as VFXConfigResponse,
      etag: this.etag,
      error: null,
      notModified: false,
    };
  }

  /**
   * Fetches VFX configuration from server
   * Supports ETag for conditional requests (304 Not Modified)
   */
  async fetchConfig(): Promise<VFXConfigFetchResult> {
    // Reuse current in-flight network request.
    if (this.pendingFetchPromise) {
      return this.pendingFetchPromise;
    }

    // Guard against immediate duplicate calls right after a completed fetch.
    if (
      this.lastResolvedResult &&
      Date.now() - this.lastFetchTime < FETCH_DEDUPE_WINDOW_MS
    ) {
      return this.lastResolvedResult;
    }

    this.pendingFetchPromise = this.fetchConfigInternal();
    try {
      const result = await this.pendingFetchPromise;
      this.lastResolvedResult = result;
      return result;
    } finally {
      this.pendingFetchPromise = null;
    }
  }

  private async fetchConfigInternal(): Promise<VFXConfigFetchResult> {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Add ETag if available for conditional request
      if (this.etag) {
        headers['If-None-Match'] = this.etag;
      }

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.VFX.CONFIG), {
        method: 'GET',
        headers,
        cache: 'no-cache',
      });

      // Handle 304 Not Modified - config hasn't changed
      if (response.status === 304) {
        this.lastFetchTime = Date.now();
        return {
          success: true,
          config: null, // No new config, keep existing
          etag: this.etag,
          error: null,
          notModified: true,
        };
      }

      // Handle other error statuses
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.warn('[VFXConfigService] Failed to fetch config:', response.status, errorText);
        const offline = this.tryOfflineVfxConfig();
        if (offline) {
          if (import.meta.env.DEV) console.log('[VFXConfigService] Using offline cache for vfx-config');
          return offline;
        }
        return {
          success: false,
          config: null,
          etag: this.etag,
          error: `HTTP ${response.status}: ${errorText}`,
          notModified: false,
        };
      }

      // Parse response
      const responseData = await response.json();

      // Debug: Log raw API response (dev only)
      if (import.meta.env.DEV) console.log('[VFXConfigService] Raw API response:', JSON.stringify(responseData, null, 2));

      // Extract ETag from response headers
      const newEtag = response.headers.get('ETag');
      if (newEtag) {
        this.etag = newEtag;
      }

      // Validate response structure - API returns { success: true, data: {...} }
      if (!responseData.success || !responseData.data) {
        console.warn('[VFXConfigService] Invalid response structure:', responseData);
        const offline = this.tryOfflineVfxConfig();
        if (offline) return offline;
        return {
          success: false,
          config: null,
          etag: this.etag,
          error: 'Invalid response structure',
          notModified: false,
        };
      }

      // Debug: Log background.theme from raw data
      if (responseData.data?.background?.theme !== undefined) {
        const rawTheme = responseData.data.background.theme;
        if (import.meta.env.DEV) console.log(`[VFXConfigService] Raw theme: ${rawTheme} (${typeof rawTheme})`);

        // Convert string to number if needed (API might return "10" instead of 10)
        if (typeof rawTheme === 'string' && !isNaN(Number(rawTheme))) {
          if (import.meta.env.DEV) console.log(`[VFXConfigService] Converting theme string to number: ${Number(rawTheme)}`);
          responseData.data.background.theme = Number(rawTheme);
        }
      } else {
        console.warn('⚠️ [VFXConfigService] No theme in raw API response:', responseData.data?.background);
      }

      // Validate config data structure
      if (!this.validateConfig(responseData.data)) {
        const offline = this.tryOfflineVfxConfig();
        if (offline) return offline;
        return {
          success: false,
          config: null,
          etag: this.etag,
          error: 'Invalid config structure',
          notModified: false,
        };
      }

      // Debug: Log theme from API
      const configData = responseData.data as VFXConfigResponse;
      this.lastFetchTime = Date.now();

      writeOfflineApiCache(OFFLINE_CACHE_KEYS.VFX_CONFIG, responseData);

      return {
        success: true,
        config: configData,
        etag: this.etag,
        error: null,
        notModified: false,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[VFXConfigService] Error fetching config:', errorMessage);

      const offline = this.tryOfflineVfxConfig();
      if (offline) {
        if (import.meta.env.DEV) console.log('[VFXConfigService] Using offline cache after error');
        return offline;
      }

      return {
        success: false,
        config: null,
        etag: this.etag,
        error: errorMessage,
        notModified: false,
      };
    }
  }

  /**
   * Validates the structure of the config response
   */
  private validateConfig(data: any): data is VFXConfigResponse {
    if (!data || typeof data !== 'object') {
      return false;
    }

    // Validate VFX config
    if (!data.vfx || typeof data.vfx !== 'object') {
      return false;
    }
    if (typeof data.vfx.enabled !== 'boolean') {
      return false;
    }

    // VFX type is only required when enabled
    if (data.vfx.enabled) {
      if (typeof data.vfx.type !== 'string') {
        return false;
      }
      const validTypes = ['snow', 'confetti', 'fireworks', 'bubbles', 'stars',
        'particles_connect', 'absorbers', 'polygons', 'emitters', 'sparkles'];
      if (!validTypes.includes(data.vfx.type)) {
        return false;
      }
      if (!['light', 'medium', 'heavy'].includes(data.vfx.intensity)) {
        return false;
      }
    }

    // Validate background config
    if (!data.background || typeof data.background !== 'object') {
      return false;
    }

    // Validate metadata
    if (typeof data.version !== 'string') {
      return false;
    }
    // updatedAt is optional (backend may not always return it)
    if (data.updatedAt !== undefined && typeof data.updatedAt !== 'string') {
      return false;
    }
    // updatedBy can be string or null (for initial/default config)
    if (data.updatedBy !== null && typeof data.updatedBy !== 'string') {
      return false;
    }

    return true;
  }

  /**
   * Gets the polling interval in milliseconds
   */
  getPollingInterval(): number {
    return POLLING_INTERVAL_MS;
  }

  /**
   * Resets the ETag (useful for testing or forced refresh)
   */
  resetEtag(): void {
    this.etag = null;
  }

  /**
   * Gets the last fetch time
   */
  getLastFetchTime(): number {
    return this.lastFetchTime;
  }
}

// Export singleton instance
export const vfxConfigService = new VFXConfigService();

